import { Socket } from "socket.io-client";
import { DefaultEventsMap } from "socket.io-client/build/typed-events";
import { GameSubscriptionEndpoints } from "../enums/gameEndpoints";
import {
  AddVoteArgs,
  AuthGameId,
  CreateGameArgs,
  JoinToGameArgs,
  NewTaskArgs,
  NewVotingArgs,
  ReconnectToGameArgs,
  RemoveTaskArgs,
  TimerArgs,
  UpdateTaskArgs,
  UpdateUserArgs,
} from "./socket.args";
import { ResponseError } from "./socket.errors";
import {
  AddTaskResponse,
  BaseResponse,
  CreateGameResponse,
  JoinToGameResponse,
  ReconnectToGameResponse,
  SystemsResponse,
  TimerResponse,
  UpdateTaskResponse,
  UpdateUserResponse,
  VoteResponse,
} from "./socket.responses";

export const createGameEmitter = (
  socket: Socket<DefaultEventsMap, DefaultEventsMap>,
  data: CreateGameArgs
): Promise<CreateGameResponse> =>
  new Promise((resolve, reject) => {
    socket.emit(
      GameSubscriptionEndpoints.CREATE,
      data,
      (response: CreateGameResponse) => {
        if (response.error) {
          reject(new ResponseError(response.error));
        }
        resolve(response);
      }
    );
  });

export const joinGameEmitter = (
  socket: Socket<DefaultEventsMap, DefaultEventsMap>,
  data: JoinToGameArgs
): Promise<JoinToGameResponse> =>
  new Promise((resolve, reject) => {
    socket.emit(
      GameSubscriptionEndpoints.JOIN,
      data,
      (response: JoinToGameResponse) => {
        if (response.error) {
          reject(new ResponseError(response.error));
        }
        resolve(response);
      }
    );
  });

export const castVoteEmitter = (
  socket: Socket<DefaultEventsMap, DefaultEventsMap>,
  data: AddVoteArgs
): Promise<VoteResponse> =>
  new Promise((resolve, reject) => {
    socket.emit(
      GameSubscriptionEndpoints.CAST_VOTE,
      data,
      (response: VoteResponse) => {
        if (response.error) {
          reject(new ResponseError(response.error));
        }
        resolve(response);
      }
    );
  });

export const systemsEmitter = (
  socket: Socket<DefaultEventsMap, DefaultEventsMap>
): Promise<SystemsResponse> =>
  new Promise((resolve, reject) => {
    socket.emit(
      GameSubscriptionEndpoints.GET_SYSTEMS,
      (response: SystemsResponse) => {
        if (response.error) {
          reject(new ResponseError(response.error));
        }
        resolve(response);
      }
    );
  });

export const reconnectToGameEmitter = (
  socket: Socket<DefaultEventsMap, DefaultEventsMap>,
  data: ReconnectToGameArgs
): Promise<ReconnectToGameResponse> =>
  new Promise((resolve, reject) => {
    socket.emit(
      GameSubscriptionEndpoints.RECONNECT_TO_GAME,
      data,
      (response: ReconnectToGameResponse) => {
        if (response.error) {
          reject(new ResponseError(response.error));
        }
        resolve(response);
      }
    );
  });

export const createTimerEmitter = (
  socket: Socket<DefaultEventsMap, DefaultEventsMap>,
  data: TimerArgs
): Promise<TimerResponse> =>
  new Promise((resolve, reject) => {
    socket.emit(
      GameSubscriptionEndpoints.CREATE_TIMER,
      data,
      (response: TimerResponse) => {
        if (response.error) {
          reject(new ResponseError(response.error));
        }
        resolve(response);
      }
    );
  });

export const addTimeEmitter = (
  socket: Socket<DefaultEventsMap, DefaultEventsMap>,
  data: TimerArgs
): Promise<TimerResponse> =>
  new Promise((resolve, reject) => {
    socket.emit(
      GameSubscriptionEndpoints.ADD_TIME,
      data,
      (response: TimerResponse) => {
        if (response.error) {
          reject(new ResponseError(response.error));
        }
        resolve(response);
      }
    );
  });

export const cancelTimerEmitter = (
  socket: Socket<DefaultEventsMap, DefaultEventsMap>,
  data: AuthGameId
): Promise<BaseResponse> =>
  new Promise((resolve, reject) => {
    socket.emit(
      GameSubscriptionEndpoints.CANCEL_TIME,
      data,
      (response: BaseResponse) => {
        if (response.error) {
          reject(new ResponseError(response.error));
        }
        resolve(response);
      }
    );
  });

export const resetTimerEmitter = (
  socket: Socket<DefaultEventsMap, DefaultEventsMap>,
  data: AuthGameId
): Promise<BaseResponse> =>
  new Promise((resolve, reject) => {
    socket.emit(
      GameSubscriptionEndpoints.RESET_TIMER,
      data,
      (response: TimerResponse) => {
        if (response.error) {
          reject(new ResponseError(response.error));
        }
        resolve(response);
      }
    );
  });

export const updateUserEmitter = (
  socket: Socket<DefaultEventsMap, DefaultEventsMap>,
  data: UpdateUserArgs
): Promise<UpdateUserResponse> =>
  new Promise((resolve, reject) => {
    socket.emit(
      GameSubscriptionEndpoints.UPDATE_USER,
      data,
      (response: UpdateUserResponse) => {
        if (response.error) {
          reject(new ResponseError(response.error));
        }
        resolve(response);
      }
    );
  });

export const revealVotesEmitter = (
  socket: Socket<DefaultEventsMap, DefaultEventsMap>,
  data: AuthGameId
): Promise<BaseResponse> =>
  new Promise((resolve, reject) => {
    socket.emit(
      GameSubscriptionEndpoints.REVEAL_VOTES,
      data,
      (response: BaseResponse) => {
        if (response.error) {
          reject(new ResponseError(response.error));
        }
        resolve(response);
      }
    );
  });

export const startNewVotingEmitter = (
  socket: Socket<DefaultEventsMap, DefaultEventsMap>,
  data: NewVotingArgs
): Promise<BaseResponse> =>
  new Promise((resolve, reject) => {
    socket.emit(
      GameSubscriptionEndpoints.START_NEW_VOTING,
      data,
      (response: BaseResponse) => {
        if (response.error) {
          reject(new ResponseError(response.error));
        }
        resolve(response);
      }
    );
  });

export const finishGameEmitter = (
  socket: Socket<DefaultEventsMap, DefaultEventsMap>,
  data: AuthGameId
): Promise<BaseResponse> =>
  new Promise((resolve, reject) => {
    socket.emit(
      GameSubscriptionEndpoints.FINISH_GAME,
      data,
      (response: BaseResponse) => {
        if (response.error) {
          reject(new ResponseError(response.error));
        }
        resolve(response);
      }
    );
  });

export const createTaskEmitter = (
  socket: Socket<DefaultEventsMap, DefaultEventsMap>,
  data: NewTaskArgs
): Promise<AddTaskResponse> =>
  new Promise((resolve, reject) => {
    socket.emit(
      GameSubscriptionEndpoints.ADD_TASK,
      data,
      (response: AddTaskResponse) => {
        if (response.error) {
          reject(new ResponseError(response.error));
        }
        resolve(response);
      }
    );
  });

export const updateTaskEmitter = (
  socket: Socket<DefaultEventsMap, DefaultEventsMap>,
  data: UpdateTaskArgs
): Promise<UpdateTaskResponse> =>
  new Promise((resolve, reject) => {
    socket.emit(
      GameSubscriptionEndpoints.UPDATE_TASK,
      data,
      (response: UpdateTaskResponse) => {
        if (response.error) {
          reject(new ResponseError(response.error));
        }
        resolve(response);
      }
    );
  });

export const removeTaskEmitter = (
  socket: Socket<DefaultEventsMap, DefaultEventsMap>,
  data: RemoveTaskArgs
): Promise<UpdateTaskResponse> =>
  new Promise((resolve, reject) => {
    socket.emit(
      GameSubscriptionEndpoints.REMOVE_TASK,
      data,
      (response: UpdateTaskResponse) => {
        if (response.error) {
          reject(new ResponseError(response.error));
        }
        resolve(response);
      }
    );
  });


export const finalResultEmitter = (
  socket: Socket<DefaultEventsMap, DefaultEventsMap>,
  data: AuthGameId
): Promise<BaseResponse> =>
  new Promise((resolve, reject) => {
    socket.emit(
      GameSubscriptionEndpoints.CHOOSE_FINAL_RESULT,
      data,
      (response: BaseResponse) => {
        if (response.error) {
          reject(new ResponseError(response.error));
        }
        resolve(response);
      }
    );
  });
